/**
 * CancellationReasonPageV2 — redesigned cancellation-reason editor.
 *
 * Layout: PageHeader + drag-sortable list (one row per reason).
 * Each row is a v2-styled input row with drag handle, TextInput,
 * and Delete IconButton. Drag-to-reorder fires Redux update
 * immediately (parity with v1). "Add new" appends an unsaved row
 * with a temporary id; "Save" persists the whole list.
 *
 * Same `userCancellationActions` plumbing as v1:
 *   fetchAllcancellationReasons / addEditCancellationReasons /
 *   deleteCancellationReasons.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  addEditCancellationReasons,
  fetchAllcancellationReasons,
  deleteCancellationReasons,
} from '../../../../actions/userCancellationActions';
import { PageHeader, Button, IconButton, cn } from '../../../redesign';
import { TextInput } from '../../../redesign/edit';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 13, height: 13 }}>
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

function SortableRow({ item, index, onChange, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.cancellation_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-surface border border-sand flex items-center"
      data-dragging={isDragging ? 'true' : 'false'}
    >
      <span
        {...listeners}
        aria-label="Drag to reorder"
        tabIndex={0}
        className={cn(
          'grid place-items-center text-text-muted shrink-0 cursor-grab',
          'hover:text-navy active:cursor-grabbing',
          'focus:outline-none focus-visible:text-navy',
        )}
        style={{
          width: 36,
          height: 48,
          touchAction: 'none',
        }}
      >
        {DragHandleIcon}
      </span>
      <span
        className="font-mono text-text-muted text-center shrink-0"
        style={{ width: 32, fontSize: 11.5 }}
      >
        {index + 1}
      </span>
      <div style={{ flex: 1, minWidth: 0, padding: '8px 8px 8px 0' }}>
        <TextInput
          value={item.reason || ''}
          onChange={(e) => onChange(index, e.target.value)}
          placeholder="Cancellation reason text"
        />
      </div>
      <span style={{ paddingRight: 8 }}>
        <IconButton
          size="sm"
          aria-label="Remove"
          title="Remove"
          onClick={(e) => onDelete(e, item.cancellation_id, index)}
          type="button"
        >
          {TrashIconSvg}
        </IconButton>
      </span>
    </div>
  );
}

export default function CancellationReasonPageV2() {
  const dispatch = useDispatch();
  const fromRedux = useSelector(
    (s) => s.cancellation_reasons.cancellation_reasons || [],
  );

  const [items, setItems] = useState([]);

  useEffect(() => {
    dispatch(fetchAllcancellationReasons());
  }, [dispatch]);

  useEffect(() => {
    if (fromRedux.length) setItems(fromRedux);
  }, [fromRedux]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.cancellation_id === active.id);
    const newIndex = items.findIndex((it) => it.cancellation_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    dispatch(addEditCancellationReasons(next))
      .then(() =>
        Swal.fire({
          icon: 'success',
          title: 'Sort order updated',
          timer: 1200,
          showConfirmButton: false,
        }),
      )
      .catch(() => setItems(items)); // revert on failure
  };

  const handleAdd = () => {
    const newItem = {
      reason: '',
      sort_order: items.length + 1,
      cancellation_id: `new-${Date.now()}`,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const handleChange = (index, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], reason: value };
      return next;
    });
  };

  const handleDelete = (e, ref_id, index) => {
    e?.preventDefault?.();
    if (ref_id && !ref_id.toString().startsWith('new-')) {
      dispatch(deleteCancellationReasons(ref_id));
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    dispatch(addEditCancellationReasons(items))
      .then(() =>
        Swal.fire({
          icon: 'success',
          title: 'Cancellation reasons saved',
          timer: 1200,
          showConfirmButton: false,
        }),
      )
      .catch(() => { /* errorHandler already shows a Swal */ });
  };

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Users"
        title="Cancellation Reasons"
        total={`${items.length} options`}
        actions={
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button
              variant="ghost"
              leadingIcon={PlusSign}
              onClick={handleAdd}
            >
              Add new
            </Button>
            <Button
              variant="coral"
              leadingIcon={SaveIconSvg}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        }
      />

      <div style={{ padding: '0 28px 32px', maxWidth: 760 }}>
        <div className="text-text-muted" style={{ fontSize: 11.5, margin: '0 4px 8px' }}>
          Drag any row to reorder
        </div>
        {items.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            No reasons yet — click "Add new" to create one.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((it) => it.cancellation_id)}
              strategy={verticalListSortingStrategy}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {items.map((it, idx) => (
                  <SortableRow
                    key={it.cancellation_id}
                    item={it}
                    index={idx}
                    onChange={handleChange}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
